import {
  collectionGroup,
  onSnapshot,
  query,
  where,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import { groupLinkedParticipants, formatMembershipProfileValue } from '@citizen-walk/shared-types';
import { db } from '../../lib/firebase';

/**
 * MembershipPicker (Sprint BG-DataSettings 10、Phase 5、GAP A-S22)。
 *
 * 旧 v1 では「session 参加者を Membership.joinedSessionIds から callable で検出」していたが、
 * v2 admin UI では「地区に参加している全 user から admin が手動 multi-select」する方針に簡略化。
 * 必要な場合は session フィルタを後で追加する想定 (forward-compatible)。
 *
 * 認可: rules で master / district admin は collectionGroup('memberships').where('districtId','==',X)
 *   経由で他人の membership を read 可能な前提 (admin export と同 pattern)。
 *
 * Firestore path: participants/{uid}/memberships/{districtId}
 *   - membership doc に `districtId` field がある (importParticipants.ts と一致)
 *
 * **2026-09-07**: 表示・選択の単位を「端末」から「人」に変更 (R-70)。端末の引き継ぎ・復帰をすると
 * membership doc は端末の数だけできるため、そのままでは同じ人が何行も並び、同じ人に何度も配る事故が
 * 起きうる。`linkedFromUids` の連結成分で 1 人にまとめ (shared-types `groupLinkedParticipants`)、
 * 配分先は代表端末 (最後に使った端末) の uid にする。サーバ側の予約実行 (assignmentEngine) と同じ規則。
 */

export interface MembershipItem {
  uid: string;
  nickname: string;
  gender: string | null;
  ageGroup: string | null;
  joinedAt: Date | null;
  /** 参加セッション (joinedSessionIds + mainSessionId)。REQ-4.3.20 の絞り込みに使う。 */
  joinedSessionIds: string[];
  /** R-70: 同一人物の別端末の uid (向きは問わない)。 */
  linkedFromUids: string[];
  /** 最終アクセス (ミリ秒)。同一人物の代表端末の選定に使う。 */
  lastSeenAtMs: number | null;
}

/**
 * 表示・選択の単位 = **人**。端末の引き継ぎ・復帰をした参加者は membership doc が端末の数だけ
 * できるため (R-70)、そのまま並べると同じ人が何行も出て、同じ人に何度も配ってしまう。
 * `linkedFromUids` の連結成分で 1 人にまとめ、配分先は代表端末 (最後に使った端末) の uid にする。
 */
export interface ParticipantRow {
  /** 代表 uid (配分先)。 */
  uid: string;
  /** この人の全 uid (代表を含む)。既存の選択に旧端末の uid が入っていても拾えるようにする。 */
  allUids: string[];
  nickname: string;
  gender: string | null;
  ageGroup: string | null;
  /** 全端末の参加セッションの和集合。 */
  joinedSessionIds: string[];
  /** 端末数 (2 以上なら引き継ぎ・復帰をした人)。 */
  deviceCount: number;
}

/** membership 行 (端末単位) を人単位に畳む (pure)。 */
export function toParticipantRows(items: readonly MembershipItem[]): ParticipantRow[] {
  return groupLinkedParticipants(items).map((g) => {
    const sessions = new Set<string>();
    for (const row of g.rows) for (const sid of row.joinedSessionIds) sessions.add(sid);
    return {
      uid: g.primaryUid,
      allUids: g.uids,
      nickname: g.primary.nickname,
      gender: g.primary.gender,
      ageGroup: g.primary.ageGroup,
      joinedSessionIds: [...sessions],
      deviceCount: g.rows.length,
    };
  });
}

export interface MembershipPickerProps {
  districtId: string;
  /** 選択中の user UID 配列 */
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  /**
   * REQ-4.3.20: 対象セッションの参加者だけに絞る (空 / 未指定は絞らない)。
   * 指定セッションのいずれか 1 つ以上に参加していれば対象 (旧2 `detectParticipants` 踏襲)。
   */
  sessionIds?: string[];
  /** セッション ID → 表示名。行に参加セッション名を出すために使う (任意)。 */
  sessionNameById?: Record<string, string>;
}

/** path `participants/{uid}/memberships/{districtId}` から uid を抽出。 */
export function extractUidFromMembershipPath(path: string): string | null {
  const m = path.match(/^participants\/([^/]+)\/memberships\/[^/]+$/);
  return m?.[1] ?? null;
}

function mapMembership(snap: {
  ref: { path: string };
  data: () => DocumentData;
}): MembershipItem | null {
  const uid = extractUidFromMembershipPath(snap.ref.path);
  if (!uid) return null;
  const data = snap.data();
  const joinedRaw = data.createdAt ?? data.joinedAt;
  const joinedAt =
    joinedRaw && typeof (joinedRaw as { toDate?: () => Date }).toDate === 'function'
      ? (joinedRaw as { toDate: () => Date }).toDate()
      : null;
  // 参加セッション: joinedSessionIds + mainSessionId (Session main 統合前後どちらの端末でも拾う)。
  const sessions = new Set<string>();
  if (Array.isArray(data.joinedSessionIds)) {
    for (const sid of data.joinedSessionIds) if (typeof sid === 'string' && sid) sessions.add(sid);
  }
  if (typeof data.mainSessionId === 'string' && data.mainSessionId)
    sessions.add(data.mainSessionId);
  const lastSeenRaw = data.lastSeenAt;
  const lastSeenAtMs =
    lastSeenRaw && typeof (lastSeenRaw as { toMillis?: () => number }).toMillis === 'function'
      ? (lastSeenRaw as { toMillis: () => number }).toMillis()
      : null;
  return {
    uid,
    nickname: typeof data.nickname === 'string' ? data.nickname : '',
    // 専用欄は項目の型に応じて配列等のこともあるので、表示用の文字にする (複数選択は「 / 」区切り)。
    gender: formatMembershipProfileValue(data.gender) || null,
    ageGroup: formatMembershipProfileValue(data.ageGroup) || null,
    joinedAt,
    joinedSessionIds: [...sessions],
    linkedFromUids: Array.isArray(data.linkedFromUids)
      ? (data.linkedFromUids as unknown[]).filter((x): x is string => typeof x === 'string' && !!x)
      : [],
    lastSeenAtMs,
  };
}

/**
 * 参加者が対象セッションの範囲に入るか (REQ-4.3.20、pure)。
 *
 * scope が空なら絞り込みなし。指定セッションの**いずれか 1 つ以上**に参加していれば対象
 * (旧2 `detectParticipants` 踏襲)。投稿側と違い、どこにも参加していない人は対象外。
 */
export function isMemberInSessionScope(
  joinedSessionIds: readonly string[],
  scope: ReadonlySet<string>,
): boolean {
  if (scope.size === 0) return true;
  return joinedSessionIds.some((sid) => scope.has(sid));
}

export function MembershipPicker({
  districtId,
  value,
  onChange,
  disabled = false,
  sessionIds,
  sessionNameById,
}: MembershipPickerProps) {
  const [items, setItems] = useState<MembershipItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    if (!districtId) return;
    setLoading(true);
    const q = query(collectionGroup(db, 'memberships'), where('districtId', '==', districtId));
    const unsub: Unsubscribe = onSnapshot(
      q,
      (snap) => {
        const xs = snap.docs.map(mapMembership).filter((x): x is MembershipItem => x !== null);
        xs.sort((a, b) => a.nickname.localeCompare(b.nickname));
        setItems(xs);
        setLoading(false);
      },
      (err) => {
        setError(err);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [districtId]);

  const sessionScope = useMemo(() => new Set(sessionIds ?? []), [sessionIds]);

  // R-70: 端末単位の membership を人単位に畳む (同じ人が端末の数だけ並ぶのを防ぐ)。
  const people = useMemo(() => toParticipantRows(items), [items]);

  const filtered = useMemo(() => {
    const lower = filter.trim().toLowerCase();
    return people.filter((p) => {
      // REQ-4.3.20: 対象セッション指定時は「いずれか 1 つ以上に参加」している人だけ。
      if (!isMemberInSessionScope(p.joinedSessionIds, sessionScope)) return false;
      if (!lower) return true;
      return (
        p.nickname.toLowerCase().includes(lower) ||
        p.allUids.some((u) => u.toLowerCase().includes(lower))
      );
    });
  }, [people, filter, sessionScope]);

  const selectedSet = useMemo(() => new Set(value), [value]);
  /** 旧端末の uid で保存されている選択も「その人が選ばれている」とみなす。 */
  const isSelected = (p: ParticipantRow) => p.allUids.some((u) => selectedSet.has(u));

  /** 選択済みのうち「対象セッションに参加していない」参加者 (REQ-4.3.20)。 */
  const outOfScopeSelected = useMemo(() => {
    if (sessionScope.size === 0) return [];
    return people.filter(
      (p) =>
        p.allUids.some((u) => selectedSet.has(u)) &&
        !isMemberInSessionScope(p.joinedSessionIds, sessionScope),
    );
  }, [people, selectedSet, sessionScope]);

  function dropOutOfScope() {
    const drop = new Set(outOfScopeSelected.flatMap((p) => p.allUids));
    onChange(value.filter((v) => !drop.has(v)));
  }

  function toggle(person: ParticipantRow) {
    if (isSelected(person)) {
      const drop = new Set(person.allUids);
      onChange(value.filter((v) => !drop.has(v)));
    } else {
      // 配分先は代表端末 (最後に使った端末) の uid。旧端末に配ると本人の手元で見えない恐れがある。
      onChange([...value, person.uid]);
    }
  }

  function selectAllFiltered() {
    const next = new Set(value);
    for (const p of filtered) if (!p.allUids.some((u) => next.has(u))) next.add(p.uid);
    onChange(Array.from(next));
  }

  function clearAll() {
    onChange([]);
  }

  /** 選択中の人数 (旧端末 uid の重複を数えない)。 */
  const selectedPeopleCount = people.filter((p) =>
    p.allUids.some((u) => selectedSet.has(u)),
  ).length;

  return (
    <div className="grid grid-cols-1 gap-2" data-testid="membership-picker">
      <div className="flex items-center gap-2 flex-wrap">
        <input
          type="search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="ニックネーム / UID で絞り込み"
          aria-label="参加者フィルタ"
          disabled={disabled}
          className="flex-1 min-w-[180px] min-h-touch px-3 border border-slate-300 rounded text-sm"
        />
        <button
          type="button"
          onClick={selectAllFiltered}
          disabled={disabled || filtered.length === 0}
          className="text-xs text-primary-700 hover:underline disabled:opacity-40"
        >
          表示中を全選択
        </button>
        <button
          type="button"
          onClick={clearAll}
          disabled={disabled || value.length === 0}
          className="text-xs text-slate-600 hover:underline disabled:opacity-40"
        >
          全解除
        </button>
      </div>
      <p className="text-[11px] text-slate-500">
        {selectedPeopleCount} 名選択中 / 表示 {filtered.length} 名 / 全 {people.length} 名
        {people.length < items.length && (
          <>（端末の引き継ぎ・復帰をした方は 1 名にまとめています）</>
        )}
        {sessionScope.size > 0 && <>（対象セッションの参加者で絞り込み中）</>}
      </p>
      {outOfScopeSelected.length > 0 && (
        <p className="text-[11px] text-warning-700">
          選択中の {outOfScopeSelected.length} 名は対象セッションに参加していません。
          <button
            type="button"
            onClick={dropOutOfScope}
            disabled={disabled}
            className="ml-1 underline hover:no-underline disabled:opacity-40"
          >
            選択から外す
          </button>
        </p>
      )}
      {loading && <p className="text-xs text-slate-500">読込中…</p>}
      {error && <p className="text-xs text-danger-700">読込エラー: {error.message}</p>}
      {!loading && filtered.length === 0 && (
        <p className="text-xs text-slate-500 italic">該当参加者がいません。</p>
      )}
      {filtered.length > 0 && (
        <ul
          className="max-h-72 overflow-auto border border-slate-200 rounded bg-white"
          role="listbox"
          aria-multiselectable="true"
        >
          {filtered.map((m) => {
            const checked = isSelected(m);
            return (
              <li key={m.uid} className="border-b border-slate-100 last:border-b-0">
                <label
                  className={`flex items-start gap-2 px-2 py-1.5 cursor-pointer hover:bg-slate-50 ${
                    checked ? 'bg-primary-50' : ''
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(m)}
                    disabled={disabled}
                    aria-label={`参加者 ${m.nickname || m.uid} を選択`}
                    className="mt-1"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-slate-800 truncate">
                      {m.nickname || '(匿名)'}
                      {m.deviceCount > 1 && (
                        <span className="ml-1 text-[11px] text-slate-500">
                          （端末 {m.deviceCount} 台）
                        </span>
                      )}
                    </p>
                    <p className="text-[11px] text-slate-500 truncate">
                      ユーザーID: <code>{m.uid}</code>
                      {m.gender && <> · {m.gender}</>}
                      {m.ageGroup && <> · {m.ageGroup}</>}
                      {m.joinedSessionIds.length > 0 && sessionNameById && (
                        <>
                          {' '}
                          ·{' '}
                          {m.joinedSessionIds
                            .map((sid) => sessionNameById[sid])
                            .filter((n): n is string => !!n)
                            .join(' / ')}
                        </>
                      )}
                    </p>
                  </div>
                </label>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
